import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip } from "@/components/chart";
import CalendarDateRangePicker from "@/components/data-range-picker";
import { LoadingSpinner } from "@/components/loading-spinner";
import { CalendarDates } from "@/custom-types";
import { useExperimentTimelineSummary } from "@/http/api-hooks";
import { type LearningLoopClosureExperiment } from "@/http/api-types";
import { useUrlParams } from "@/http/use-url-params";
import { formatDateToYYYYMMDD, formatUTCDateTime } from "@/lib/utils";
import { subDays } from "date-fns";
import { Clock, FileText, FlaskConical, Rocket, TimerReset, type LucideIcon } from "lucide-react";
import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, ReferenceLine, YAxis } from "recharts";
import { formatLearningLoopDuration } from "./components/tabs/summary-tab";

const LEARNING_LOOP_MAX_BAR_SIZE = 56;

type LearningLoopClosureStageRow = {
  label: string;
  experiment_id: number;
  experiment_name: string;
  start_date: string;
  end_date: string;
  runtime_seconds: number;
  runtime_days: number;
  closure_seconds: number;
  closure_days: number;
  end_to_conclusion_seconds: number;
  end_to_conclusion_days: number;
  conclusion_to_rollout_seconds: number;
  conclusion_to_rollout_days: number;
};

type LearningLoopClosureTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: LearningLoopClosureStageRow }>;
};

type LearningLoopRuntimeTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: LearningLoopClosureStageRow }>;
};

type LearningLoopClosureBarClickData = {
  payload?: LearningLoopClosureStageRow;
};

function secondsToDays(seconds?: number): number {
  if (seconds === undefined || Number.isNaN(seconds)) {
    return 0;
  }
  return Number((Math.max(0, seconds) / 86400).toFixed(2));
}

function getExperimentDisplayName(experiment: LearningLoopClosureExperiment): string {
  const name = experiment.experiment_name.trim();
  return name.length > 0 ? name : `Experiment ${experiment.experiment_id}`;
}

function getExperimentSummaryHref(experimentID: number): string {
  return `/experiments/${experimentID}?tab=summary`;
}

function LearningLoopClosureTooltip({ active, payload }: LearningLoopClosureTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }

  return (
    <div className="min-w-64 max-w-sm overflow-hidden rounded-md bg-primary px-3 py-2 text-xs text-primary-foreground">
      <div className="font-medium">{row.experiment_name}</div>
      <div className="mt-0.5 text-[11px] text-primary-foreground/70">Experiment ID: {row.experiment_id}</div>
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">Total closure</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatLearningLoopDuration(row.closure_seconds)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">End to conclusion</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatLearningLoopDuration(row.end_to_conclusion_seconds)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">Conclusion to roll out</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatLearningLoopDuration(row.conclusion_to_rollout_seconds)}
          </span>
        </div>
      </div>
    </div>
  );
}

function LearningLoopRuntimeTooltip({ active, payload }: LearningLoopRuntimeTooltipProps) {
  const row = payload?.[0]?.payload;
  if (!active || !row) {
    return null;
  }

  return (
    <div className="min-w-64 max-w-sm overflow-hidden rounded-md bg-primary px-3 py-2 text-xs text-primary-foreground">
      <div className="font-medium">{row.experiment_name}</div>
      <div className="mt-0.5 text-[11px] text-primary-foreground/70">Experiment ID: {row.experiment_id}</div>
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">Runtime</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatLearningLoopDuration(row.runtime_seconds)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">Start</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatUTCDateTime(row.start_date)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-primary-foreground/70">End</span>
          <span className="font-mono font-medium tabular-nums text-primary-foreground">
            {formatUTCDateTime(row.end_date)}
          </span>
        </div>
      </div>
    </div>
  );
}

function LearningLoopMetricCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: string | number;
  description: string;
  icon: LucideIcon;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="font-mono text-2xl font-bold tabular-nums">{value}</div>
        <p className="text-xs font-normal text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  );
}

export default function ExperimentsRuntimePage() {
  const { paramState, updateDateRange } = useUrlParams({
    fromDate: {
      name: "from",
      type: "date",
      defaultValue: subDays(new Date(), 30),
    },
    toDate: {
      name: "to",
      type: "date",
      defaultValue: new Date(),
    },
  });

  const currentDate: CalendarDates = {
    from: paramState.fromDate as Date,
    to: paramState.toDate as Date,
  };
  const from = currentDate?.from ? formatDateToYYYYMMDD(currentDate.from) : undefined;
  const to = currentDate?.to ? formatDateToYYYYMMDD(currentDate.to) : undefined;
  const shouldFetchTimelineSummary = Boolean(from && to);

  const {
    data: timelineSummaryResponse,
    isLoading: timelineSummaryLoading,
    error: timelineSummaryError,
  } = useExperimentTimelineSummary(
    {
      from,
      to,
    },
    shouldFetchTimelineSummary,
  );

  const learningLoopClosureBaseline = timelineSummaryResponse?.data?.learning_loop_closure_baseline;
  const learningLoopClosureStageData = useMemo<LearningLoopClosureStageRow[]>(
    () =>
      (learningLoopClosureBaseline?.experiments || []).map(experiment => ({
        label: String(experiment.experiment_id),
        experiment_id: experiment.experiment_id,
        experiment_name: getExperimentDisplayName(experiment),
        start_date: experiment.start_date,
        end_date: experiment.end_date,
        runtime_seconds: experiment.runtime_seconds,
        runtime_days: secondsToDays(experiment.runtime_seconds),
        closure_seconds: experiment.closure_seconds,
        closure_days: secondsToDays(experiment.closure_seconds),
        end_to_conclusion_seconds: experiment.end_to_conclusion_seconds,
        end_to_conclusion_days: secondsToDays(experiment.end_to_conclusion_seconds),
        conclusion_to_rollout_seconds: experiment.conclusion_to_rollout_seconds,
        conclusion_to_rollout_days: secondsToDays(experiment.conclusion_to_rollout_seconds),
      })),
    [learningLoopClosureBaseline?.experiments],
  );
  const learningLoopClosureChartMinWidth = Math.max(720, learningLoopClosureStageData.length * 72);

  const handleLearningLoopBarClick = (data: LearningLoopClosureBarClickData) => {
    const experimentID = data.payload?.experiment_id;
    if (experimentID === undefined) {
      return;
    }

    window.open(getExperimentSummaryHref(experimentID), "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      <section className="space-y-6">
        <div className="max-w-3xl">
          <h1 className="text-2xl font-bold">Experiment Runtime and Learning Loop Closure</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Experiment runtime and post-experiment time from runtime end to conclusion and rollout decision timestamp.
          </p>
        </div>

        <div className="w-full max-w-md">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Rolled Out Between
          </div>
          <CalendarDateRangePicker
            className="w-full"
            date={currentDate}
            presetVariant="longRange"
            setDate={selectedDate => {
              updateDateRange(selectedDate, "fromDate", "toDate");
            }}
          />
        </div>

        {timelineSummaryLoading ? (
          <div className="w-full flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : timelineSummaryError ? (
          <div className="text-center text-red-600 py-8">Failed to load learning-loop closure data.</div>
        ) : learningLoopClosureBaseline && learningLoopClosureStageData.length > 0 ? (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <LearningLoopMetricCard
                title="Experiments"
                value={learningLoopClosureBaseline.experiment_count}
                description="Closed loops in selected range"
                icon={FlaskConical}
              />
              <LearningLoopMetricCard
                title="Median Runtime"
                value={formatLearningLoopDuration(learningLoopClosureBaseline.median_runtime_seconds)}
                description="Experiment start to runtime end"
                icon={Clock}
              />
              <LearningLoopMetricCard
                title="Median Closure"
                value={formatLearningLoopDuration(learningLoopClosureBaseline.median_closure_seconds)}
                description="Runtime end to rollout decision"
                icon={TimerReset}
              />
              <LearningLoopMetricCard
                title="End To Conclusion"
                value={formatLearningLoopDuration(learningLoopClosureBaseline.median_end_to_conclusion_seconds)}
                description="Runtime end to conclusion"
                icon={FileText}
              />
              <LearningLoopMetricCard
                title="Conclusion To Roll Out"
                value={formatLearningLoopDuration(learningLoopClosureBaseline.median_conclusion_to_rollout_seconds)}
                description="Conclusion to rollout decision"
                icon={Rocket}
              />
            </div>

            <div className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold">Closure Stages</h2>
                <p className="text-sm text-muted-foreground">
                  Time from runtime end to conclusion, then from conclusion to rollout decision.
                </p>
              </div>
              <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
                <ChartContainer
                  className="h-80 w-full"
                  style={{ minWidth: learningLoopClosureChartMinWidth }}
                  config={{
                    end_to_conclusion_days: { label: "End to conclusion (days)", color: "#2563eb" },
                    conclusion_to_rollout_days: { label: "Conclusion to roll out (days)", color: "#16a34a" },
                  }}
                >
                  <BarChart data={learningLoopClosureStageData} margin={{ top: 8, right: 16, left: 8, bottom: 48 }}>
                    <CartesianGrid vertical={false} />

                    <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                    <ReferenceLine
                      y={14}
                      label={{ value: "<=14 Days", fill: "hsl(var(--foreground))", position: "insideTop", offset: 15 }}
                      stroke="hsl(var(--foreground))"
                      strokeDasharray="4 4"
                    />
                    <ChartTooltip content={<LearningLoopClosureTooltip />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      dataKey="end_to_conclusion_days"
                      fill="var(--color-end_to_conclusion_days)"
                      stackId="stage"
                      maxBarSize={LEARNING_LOOP_MAX_BAR_SIZE}
                      cursor="pointer"
                      onClick={handleLearningLoopBarClick}
                    />
                    <Bar
                      dataKey="conclusion_to_rollout_days"
                      fill="var(--color-conclusion_to_rollout_days)"
                      stackId="stage"
                      maxBarSize={LEARNING_LOOP_MAX_BAR_SIZE}
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={handleLearningLoopBarClick}
                    />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold">Experiment Runtime</h2>
                <p className="text-sm text-muted-foreground">Runtime from experiment start to runtime end.</p>
              </div>
              <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
                <ChartContainer
                  className="h-80 w-full"
                  style={{ minWidth: learningLoopClosureChartMinWidth }}
                  config={{
                    runtime_days: { label: "Runtime (days)", color: "#f97316" },
                  }}
                >
                  <BarChart data={learningLoopClosureStageData} margin={{ top: 8, right: 16, left: 8, bottom: 48 }}>
                    <CartesianGrid vertical={false} />

                    <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                    <ReferenceLine
                      y={14}
                      label={{
                        value: ">= 14 Days",
                        fill: "hsl(var(--foreground))",
                        position: "insideTop",
                        offset: -15,
                      }}
                      stroke="hsl(var(--foreground))"
                      strokeDasharray="4 4"
                    />
                    <ChartTooltip content={<LearningLoopRuntimeTooltip />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar
                      dataKey="runtime_days"
                      fill="var(--color-runtime_days)"
                      maxBarSize={LEARNING_LOOP_MAX_BAR_SIZE}
                      radius={[4, 4, 0, 0]}
                      cursor="pointer"
                      onClick={handleLearningLoopBarClick}
                    />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">No fully closed learning loops found.</div>
        )}
      </section>
    </div>
  );
}
